import i18n from '@/locales/i18n';
import { showCreditClaimToast } from '@/composables/useCreditClaimToast';
import { resolveDepositBonus } from '@/domain/deposit/deposit-bonus-campaign';

function readErrMessage(err) {
  return String(err?.message ?? err?.msg ?? '').trim();
}

/** 首次充值 pending toast 后，短暂抑制「领取成功」避免文案打架 */
let firstDepositCreditsPendingAt = 0;
const FIRST_DEPOSIT_CLAIM_TOAST_SUPPRESS_MS = 90_000;

function shouldSuppressImmediateCreditClaimToast() {
  return (
    firstDepositCreditsPendingAt > 0 &&
    Date.now() - firstDepositCreditsPendingAt < FIRST_DEPOSIT_CLAIM_TOAST_SUPPRESS_MS
  );
}

/**
 * 首次充值到账：体验金 / 奖励金将在约一分钟内入账。
 */
export function notifyFirstDepositCreditsPending() {
  firstDepositCreditsPendingAt = Date.now();
  try {
    showCreditClaimToast(i18n.global.t('deposit.firstDepositCreditsPendingToast'), {
      dedupeKey: 'first-deposit-credits-pending',
      durationMs: 3600,
    });
  } catch {
    /* i18n soft-fail */
  }
}

/** 体验金到账：居中文字弹窗 */
export function notifyTrialFundAutoClaimSuccess() {
  if (shouldSuppressImmediateCreditClaimToast()) return;
  try {
    showCreditClaimToast(i18n.global.t('trialFund.claimSuccessToast'), {
      dedupeKey: 'trial-fund-claim-success',
    });
  } catch {
    /* i18n soft-fail */
  }
}

/** 充值奖励金到账：居中文字弹窗 */
export function notifyDepositBonusClaimSuccess() {
  if (shouldSuppressImmediateCreditClaimToast()) return;
  try {
    showCreditClaimToast(i18n.global.t('deposit.bonusClaimSuccessToast'), {
      dedupeKey: 'deposit-bonus-claim-success',
    });
  } catch {
    /* i18n soft-fail */
  }
}

/**
 * 入账金额命中充值赠送档时弹出「奖励金领取成功」。
 * @param {unknown} usdAmount
 */
export function notifyDepositBonusClaimSuccessIfEligible(usdAmount) {
  if (!resolveDepositBonus(usdAmount)) return;
  notifyDepositBonusClaimSuccess();
}

/**
 * Map trial claim API errors to user-facing copy (never raw「系统内部错误 (500)」).
 * @param {unknown} err
 * @returns {string}
 */
export function resolveTrialClaimUserMessage(err) {
  const t = i18n.global.t;
  const raw = readErrMessage(err);
  const code = Number(err?.code);

  if (/already\s*claim|已经领取/i.test(raw)) {
    return '';
  }
  if (
    code === 400 &&
    /首次充值|deposit_required|trial\.deposit_required|再领取体验金|complete.*deposit.*claim/i.test(
      raw
    )
  ) {
    return t('trialFund.claimNeedDeposit');
  }
  if (/首次充值|deposit_required|trial\.deposit_required|再领取体验金/i.test(raw)) {
    return t('trialFund.claimNeedDeposit');
  }
  if (code === 503 || /grant\s*pending|正在发放|mint_pending|try\s*again/i.test(raw)) {
    return t('trialFund.claimPending');
  }
  if (/timeout|504|gateway\s*timeout/i.test(raw)) {
    return t('trialFund.claimPending');
  }
  if (/internal|系统内部|\(500\)/i.test(raw)) {
    return t('trialFund.claimRetryLater');
  }
  if (raw) {
    return raw;
  }
  return t('trialFund.claimRetryLater');
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isTrialClaimDepositRequiredError(err) {
  const raw = readErrMessage(err);
  if (
    Number(err?.code) === 400 &&
    /首次充值|deposit_required|trial\.deposit_required|再领取体验金/i.test(raw)
  ) {
    return true;
  }
  return /首次充值|deposit_required|trial\.deposit_required|再领取体验金/i.test(raw);
}
